import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, Clock, Shield, MapPin } from 'lucide-react';

export const ShippingInfo = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link 
              to="/" 
              className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Shipping Information</h1>
            <p className="text-gray-600">Everything you need to know about our shipping policies</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Shipping Options</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Truck className="h-6 w-6 text-primary-600 mr-3" />
                    <h3 className="text-lg font-semibold">Standard Shipping</h3>
                  </div>
                  <p className="text-gray-700 mb-2">Delivery within 5-7 business days</p>
                  <p className="text-sm text-gray-600">Free on orders above ₹999</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Clock className="h-6 w-6 text-primary-600 mr-3" />
                    <h3 className="text-lg font-semibold">Express Shipping</h3>
                  </div>
                  <p className="text-gray-700 mb-2">Delivery within 2-3 business days</p>
                  <p className="text-sm text-gray-600">Additional charges apply</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Shipping Areas</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We currently ship to all major cities and towns across India. Our shipping coverage includes:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Metro Cities</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Mumbai</li>
                    <li>• Delhi</li>
                    <li>• Bangalore</li>
                    <li>• Chennai</li>
                    <li>• Kolkata</li>
                    <li>• Hyderabad</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Tier 2 Cities</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Pune</li>
                    <li>• Ahmedabad</li>
                    <li>• Jaipur</li>
                    <li>• Lucknow</li>
                    <li>• Kochi</li>
                    <li>• Chandigarh</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Other Areas</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• All state capitals</li>
                    <li>• Major district towns</li>
                    <li>• Rural areas (with restrictions)</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Shipping Process</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Order Processing</h3>
                    <p className="text-gray-700">We process your order within 1-2 business days</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Packaging</h3>
                    <p className="text-gray-700">Your items are carefully packaged for safe delivery</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">3</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Dispatch</h3>
                    <p className="text-gray-700">Your order is handed over to our shipping partner</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">4</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Delivery</h3>
                    <p className="text-gray-700">Your order is delivered to your doorstep</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Tracking Your Order</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Once your order is dispatched, you will receive a tracking number via SMS and email. 
                You can track your order status in real-time using our tracking system.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800">
                  <strong>Note:</strong> You can track your order by visiting our website and entering 
                  your order number or tracking ID.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Delivery Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Shield className="h-5 w-5 text-primary-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Safe Delivery</h3>
                      <p className="text-sm text-gray-700">All packages are handled with care</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-primary-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Address Verification</h3>
                      <p className="text-sm text-gray-700">Please ensure your address is correct</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-primary-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Delivery Hours</h3>
                      <p className="text-sm text-gray-700">Monday to Saturday, 9 AM to 7 PM</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Truck className="h-5 w-5 text-primary-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Delivery Attempts</h3>
                      <p className="text-sm text-gray-700">Up to 3 delivery attempts</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about shipping, please contact our customer service team:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> contact@nineeyeshop.com<br />
                  <strong>Phone:</strong> +91 7094974571<br />
                  <strong>Address:</strong> 1st Floor, 36-07, Kamaraj Colony 2nd Street, Kodambakkam, Chennai, Chennai, Tamil Nadu, 600024
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
